import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, ArrowRight, ArrowUpRight } from "lucide-react";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";
import { SERVICES, SPECIALTY } from "@/data/site";

const MotionLink = motion.create(Link);

const SectionHeading = ({ kicker, title, subtitle }) => (
  <div className="mb-12 max-w-2xl">
    <div className="lx-kicker mb-3">{kicker}</div>
    <h2 className="font-display text-2xl font-semibold tracking-tight text-[color:var(--lx-text)] sm:text-3xl lg:text-4xl">
      {title}
    </h2>
    {subtitle && (
      <p className="mt-4 text-base leading-relaxed text-[color:var(--lx-text-2)]">
        {subtitle}
      </p>
    )}
  </div>
);

const SpecialtyCard = () => {
  const Icon = SPECIALTY.icon;
  return (
    <MotionLink
      to={SPECIALTY.to}
      variants={fadeUp}
      data-testid="services-specialty"
      className="lx-terminal group relative block overflow-hidden p-7 transition-colors sm:p-9 lg:p-10"
      style={{
        background:
          "linear-gradient(140deg, rgba(168,232,92,0.10), rgba(14,20,16,0.92) 60%)",
        borderColor: "rgba(168,232,92,0.32)",
      }}
    >
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-7">
          <div className="flex items-center gap-3">
            <span
              className="grid h-12 w-12 shrink-0 place-items-center rounded-[14px]"
              style={{ background: "rgba(168,232,92,0.14)", color: "var(--lx-green)" }}
            >
              <Icon size={24} />
            </span>
            <span
              data-testid="services-specialty-badge"
              className="rounded-full border px-3 py-1 font-mono-lx text-[11px] uppercase tracking-[0.16em]"
              style={{ borderColor: "rgba(168,232,92,0.4)", color: "var(--lx-green-soft)" }}
            >
              {SPECIALTY.badge}
            </span>
          </div>

          <h3 className="mt-6 font-display text-2xl font-semibold tracking-tight text-[color:var(--lx-text)] sm:text-3xl">
            {SPECIALTY.title}
          </h3>
          <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-[color:var(--lx-text-2)]">
            {SPECIALTY.desc}
          </p>

          <span
            className="mt-6 inline-flex items-center gap-2 font-display text-sm font-medium"
            style={{ color: "var(--lx-green)" }}
          >
            KI-Agenten entdecken
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </span>
        </div>

        <div className="lg:col-span-5">
          <ul className="grid grid-cols-1 gap-3">
            {SPECIALTY.points.map((p) => (
              <li key={p} className="flex items-start gap-3">
                <span
                  className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-[8px]"
                  style={{ background: "rgba(168,232,92,0.12)", color: "var(--lx-green)" }}
                >
                  <Check size={14} />
                </span>
                <span className="text-sm leading-relaxed text-[color:var(--lx-text-2)]">
                  {p}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </MotionLink>
  );
};

export const Services = () => {
  return (
    <section
      id="leistungen"
      data-testid="services-section"
      className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28"
    >
      <SectionHeading
        kicker="Leistungen"
        title="Alles, was aus einer Idee ein Produkt macht."
        subtitle="Von KI-Agenten über Mobile- und Web-Apps bis zur kompletten Plattform. Wir begleiten den ganzen Produktzyklus: Strategie, Design, Engineering, SEO und Betrieb."
      />

      <motion.div
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={viewportOnce}
        className="space-y-8"
      >
        {/* Specialty highlight */}
        <SpecialtyCard />

        {/* Detailed service cards */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s) => {
            const Icon = s.icon;
            return (
              <MotionLink
                key={s.title}
                to={`/leistungen/${s.slug}`}
                variants={fadeUp}
                data-testid="services-card"
                aria-label={`${s.title} – mehr erfahren`}
                className="lx-card group relative flex flex-col overflow-hidden p-6"
              >
                <div
                  className="absolute left-0 top-0 h-0.5 w-0 transition-all duration-300 group-hover:w-full"
                  style={{
                    background:
                      "linear-gradient(90deg, var(--lx-green) 0%, var(--lx-green-soft) 100%)",
                  }}
                />
                <div className="mb-5 flex items-center justify-between">
                  <div
                    className="grid h-11 w-11 place-items-center rounded-[12px] border"
                    style={{
                      borderColor: "var(--lx-border-strong)",
                      background: "var(--lx-surface-2)",
                      color: "var(--lx-green)",
                    }}
                  >
                    <Icon size={20} />
                  </div>
                  <ArrowUpRight
                    size={18}
                    className="text-[color:var(--lx-muted)] transition-all duration-200 group-hover:text-[color:var(--lx-green)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                  />
                </div>
                <h3 className="font-display text-lg font-semibold text-[color:var(--lx-text)]">
                  {s.title}
                </h3>
                <p className="mt-2.5 text-sm leading-relaxed text-[color:var(--lx-text-2)]">
                  {s.desc}
                </p>
                {s.points && (
                  <ul className="mt-4 space-y-2 border-t pt-4" style={{ borderColor: "var(--lx-border)" }}>
                    {s.points.map((p) => (
                      <li key={p} className="flex items-start gap-2.5">
                        <Check
                          size={15}
                          className="mt-0.5 shrink-0"
                          style={{ color: "var(--lx-green)" }}
                        />
                        <span className="text-[13px] leading-relaxed text-[color:var(--lx-text-2)]">
                          {p}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </MotionLink>
            );
          })}
        </div>
      </motion.div>
    </section>
  );
};
